import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  PurchasePotOverviewResponse,
  PurchasePotTransaction,
  AssistantTodoItem,
} from '@dreamai/types';
import api from '../../api';
import { useResolvedFileUrl } from '../shared/useResolvedFileUrl';
import TopUpPotModal from './TopUpPotModal';
import RefundPotModal from './RefundPotModal';
import DismissTodoModal from '../chat/sidebar/DismissTodoModal';
import DreamMarque from '../common/DreamMarque';
import { PAYMENT_UPDATED_PARAM } from '../../utils/hostedCheckout';
import './purchase-pot-section.scss';

const CoinsIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='20'
    height='20'
    viewBox='0 0 20 20'
    fill='none'
  >
    <path
      d='M13.2814 13.2814C16.1336 12.8996 18.3333 10.4567 18.3333 7.5C18.3333 4.27834 15.7216 1.66667 12.5 1.66667C9.54328 1.66667 7.10037 3.8664 6.71852 6.71856M6.24996 10.8333L7.49996 10V14.5833M6.24996 14.5833H8.74996M13.3333 12.5C13.3333 15.7217 10.7216 18.3333 7.49996 18.3333C4.2783 18.3333 1.66663 15.7217 1.66663 12.5C1.66663 9.27834 4.2783 6.66667 7.49996 6.66667C10.7216 6.66667 13.3333 9.27834 13.3333 12.5Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const InfoCircleIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <circle cx='8' cy='8' r='6.5' stroke='#838B9B' strokeWidth='1.5' />
    <text
      x='8'
      y='11.5'
      textAnchor='middle'
      fill='#838B9B'
      fontSize='10'
      fontWeight='600'
      fontFamily='Instrument Sans, sans-serif'
    >
      i
    </text>
  </svg>
);

const InfoOctagonIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M6.68 1.667h6.64l4.68 4.68v6.64l-4.68 4.68H6.68L2 13.32V6.68l4.68-5.013Z'
      stroke='#EE4266'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M10 6.667V10M10 13.333h.008'
      stroke='#EE4266'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

function formatCurrency(amount: number, currency: string): string {
  try {
    const isWhole = amount % 1 === 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatTxDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface PurchasePotSectionProps {
  assistantName: string;
}

const ReceiptIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <path
      d='M13.333 2v12l-1.666-1L10 14l-1.667-1L6.667 14 5 13l-1.667 1L1.667 13V2l1.666 1L5 2l1.667 1L8.333 2 10 3l1.667-1 1.666 1Z'
      stroke='currentColor'
      strokeWidth='1.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M5 6h6M5 8.667h4'
      stroke='currentColor'
      strokeWidth='1.2'
      strokeLinecap='round'
    />
  </svg>
);

interface ReceiptModalProps {
  urls: string[];
  onClose: () => void;
}

/**
 * Receipts captured by the browser agent are private files
 * (`/api/files/{fileId}`); exchange those for a short-lived signed URL before
 * rendering. Plain URLs render directly.
 */
const ReceiptImage: FC<{ url: string; index: number }> = ({ url, index }) => {
  const resolved = useResolvedFileUrl(url);

  if (resolved.status === 'failed' || resolved.status === 'private') {
    return <p className='pp-receipt-unavailable'>Receipt unavailable</p>;
  }
  if (resolved.status === 'loading') return null;
  return (
    <img
      src={resolved.src}
      alt={`Receipt ${index + 1}`}
      className='pp-receipt-img'
    />
  );
};

const ReceiptModal: FC<ReceiptModalProps> = ({ urls, onClose }) => (
  <div className='pp-receipt-overlay' onClick={onClose}>
    <div className='pp-receipt-modal' onClick={(e) => e.stopPropagation()}>
      <div className='pp-receipt-header'>
        <h3>Receipt{urls.length > 1 ? 's' : ''}</h3>
        <button className='pp-receipt-close' onClick={onClose}>
          &times;
        </button>
      </div>
      <div className='pp-receipt-body'>
        {urls.map((url, i) => (
          <ReceiptImage key={i} url={url} index={i} />
        ))}
      </div>
    </div>
  </div>
);

const PurchasePotSection: FC<PurchasePotSectionProps> = ({ assistantName }) => {
  const cardLabel = `${assistantName}\u2019s Card`;
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [data, setData] = useState<PurchasePotOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundFeedback, setRefundFeedback] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTodo, setCancelTodo] = useState<AssistantTodoItem | null>(null);
  const [showAvailableTooltip, setShowAvailableTooltip] = useState(false);
  const [receiptUrls, setReceiptUrls] = useState<string[] | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await api.get<PurchasePotOverviewResponse>(
        '/credits/purchase-pot-overview',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(res.data);
    } catch (err) {
      console.error('Error fetching card balance overview:', err);
      setError('Failed to load card data.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Back from an Airwallex card-setup redirect (e.g. 3DS): reopen the top-up
  // modal and drop the marker so a refresh doesn't repeat this.
  const returnedFromCardSetup =
    searchParams.get(PAYMENT_UPDATED_PARAM) === 'true';
  useEffect(() => {
    if (!returnedFromCardSetup) return;
    setShowTopUpModal(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PAYMENT_UPDATED_PARAM);
        return next;
      },
      { replace: true, state: location.state },
    );
  }, [returnedFromCardSetup, setSearchParams, location.state]);

  const closeTopUpModal = useCallback(() => {
    setShowTopUpModal(false);
  }, []);

  const handleCancelClick = useCallback(
    async (tx: PurchasePotTransaction) => {
      // Direct cancel via paymentId (preferred path)
      if (tx.paymentId) {
        if (
          !window.confirm(
            'Cancel this pending transaction and refund the reserved amount?',
          )
        )
          return;
        try {
          const token = await getAccessTokenSilently();
          await api.post(
            `/credits/cancel-pending-payment/${tx.paymentId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          fetchData();
        } catch (err) {
          console.error('Error cancelling pending payment:', err);
        }
        return;
      }

      // Legacy fallback: cancel via subThread/todo route
      if (tx.subThreadId) {
        const todo = { id: tx.subThreadId } as AssistantTodoItem;
        setCancelTodo(todo);
        setShowCancelModal(true);
      }
    },
    [getAccessTokenSilently, fetchData],
  );

  const handleCancelConfirm = useCallback(
    async (todo: AssistantTodoItem) => {
      setShowCancelModal(false);
      setCancelTodo(null);
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          `/todos/${todo.id}/cancel`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        fetchData();
      } catch (err) {
        console.error('Error cancelling pending transaction:', err);
      }
    },
    [getAccessTokenSilently, fetchData],
  );

  if (loading) {
    return (
      <div className='purchase-pot-section'>
        <div className='pp-header'>
          <h2 className='section-title'>{cardLabel}</h2>
          <p className='pp-subtitle'>
            Top up the card your secretary uses for purchases, and get a refund
            on any unused money.
          </p>
        </div>
        <div className='settings-skeleton' />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='settings-error'>{error || 'Something went wrong.'}</div>
    );
  }

  const isEmpty = data.balance <= 0;

  return (
    <div className='purchase-pot-section'>
      {/* ── Header ──────────────────────────────────────── */}
      <div className='pp-header'>
        <h2 className='section-title'>{cardLabel}</h2>
        <p className='pp-subtitle'>
          Top up the card your secretary uses for purchases, and get a refund on
          any unused money.
        </p>
      </div>

      <div className='pp-content-card'>
        {/* ── Warning banner (empty pot) ──────────────────── */}
        {isEmpty && (
          <div className='pp-warning-banner'>
            <div className='pp-warning-left'>
              <InfoOctagonIcon />
              <span>
                The card is empty — {assistantName} won&rsquo;t be able to make
                purchases.
              </span>
            </div>
            <button
              className='pp-warning-link'
              onClick={() => setShowTopUpModal(true)}
            >
              Top-up now
            </button>
          </div>
        )}

        {/* ── Feedback messages ──────────────────────────── */}
        {refundFeedback && (
          <p
            className={`pp-feedback ${refundFeedback.includes('failed') ? 'error' : 'success'}`}
          >
            {refundFeedback}
          </p>
        )}

        {/* ── Hero card ──────────────────────────────────── */}
        <div className='pp-hero-card'>
          <DreamMarque variant='optical-md' className='pp-hero-logo' />
          <div className='pp-hero-info'>
            <div className='pp-hero-label'>
              <CoinsIcon className='pp-hero-coins-icon' />
              <span>{cardLabel}</span>
            </div>
            <div className='pp-hero-balance'>
              {formatCurrency(data.totalBalance, data.currency)}
            </div>
            <div className='pp-hero-available'>
              <span className='pp-available-text'>
                Available {formatCurrency(data.balance, data.currency)}
              </span>
              <span
                className='pp-available-info'
                onMouseEnter={() => setShowAvailableTooltip(true)}
                onMouseLeave={() => setShowAvailableTooltip(false)}
              >
                <InfoCircleIcon />
                {showAvailableTooltip && (
                  <span className='pp-available-tooltip'>
                    <strong>Available</strong>
                    This is the amount on the card minus any pending
                    transactions.
                  </span>
                )}
              </span>
            </div>
            {data.spendCap !== null && (
              <div className='pp-hero-cap'>
                <span className='pp-cap-label'>Spend cap</span>
                <span className='pp-cap-value'>
                  {formatCurrency(data.spendCap, data.currency)}
                </span>
                <InfoCircleIcon />
              </div>
            )}
          </div>

          <div className='pp-hero-actions'>
            <button
              className='pp-btn-topup'
              onClick={() => setShowTopUpModal(true)}
            >
              Top up card
            </button>
            <button
              className='pp-btn-refund'
              onClick={() => setShowRefundModal(true)}
              disabled={isEmpty}
            >
              Refund balance
            </button>
          </div>
        </div>

        {/* ── Recent transactions ────────────────────────── */}
        <div className='pp-transactions'>
          <h3 className='pp-transactions-title'>Recent transactions</h3>

          {/* ── Summary cards ────────────────────────────── */}
          <div className='pp-summary-cards'>
            <div className='pp-summary-card'>
              <span className='pp-summary-label'>Spend last 30 days</span>
              <span className='pp-summary-value'>
                {formatCurrency(data.totalSpend30d, data.currency)}
              </span>
            </div>
            <div className='pp-summary-card pending'>
              <span className='pp-summary-label'>Pending transactions</span>
              <span className='pp-summary-value'>
                {formatCurrency(data.pendingTotal, data.currency)}
              </span>
            </div>
          </div>

          {/* ── Transaction table ────────────────────────── */}
          {data.transactions.length > 0 && (
            <div className='pp-tx-table'>
              <div className='pp-tx-thead'>
                <span className='pp-tx-th date'>Date</span>
                <span className='pp-tx-th amount'>Amount</span>
                <span className='pp-tx-th vendor'>Note</span>
                <span className='pp-tx-th balance'>Available</span>
              </div>
              <div className='pp-tx-tbody'>
                {data.transactions.map((tx) => {
                  const isPending = tx.status === 'pending';
                  return (
                    <div
                      key={tx.id}
                      className={`pp-tx-row${isPending ? ' pending' : ''}`}
                    >
                      <span className='pp-tx-td date'>
                        {formatTxDate(tx.date)}
                        {isPending && (
                          <span className='pp-pending-badge'>(Pending)</span>
                        )}
                      </span>
                      <span
                        className={`pp-tx-td amount ${tx.amount >= 0 ? 'positive' : 'negative'}`}
                      >
                        {tx.amount >= 0 ? '+' : ''}
                        {formatCurrency(Math.abs(tx.amount), data.currency)}
                        {isPending &&
                          !tx.hasActiveHumanTask &&
                          (tx.paymentId || tx.subThreadId) && (
                            <button
                              className='pp-tx-cancel'
                              onClick={() => handleCancelClick(tx)}
                            >
                              Cancel
                            </button>
                          )}
                      </span>
                      <span className='pp-tx-td vendor'>
                        {tx.vendor || '\u2014'}
                        {tx.receiptImageUrls &&
                          tx.receiptImageUrls.length > 0 && (
                            <button
                              className='pp-receipt-btn'
                              title='View receipt'
                              onClick={() =>
                                setReceiptUrls(tx.receiptImageUrls!)
                              }
                            >
                              <ReceiptIcon />
                            </button>
                          )}
                      </span>
                      <span className='pp-tx-td balance'>
                        {tx.runningBalance != null
                          ? formatCurrency(tx.runningBalance, data.currency)
                          : '\u2014'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────── */}
      <TopUpPotModal
        isOpen={showTopUpModal}
        onClose={closeTopUpModal}
        topUpOption={data.topUpOption}
        assistantName={assistantName}
        onSuccess={() => {
          closeTopUpModal();
          fetchData();
        }}
      />
      <RefundPotModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        balance={data.balance}
        currency={data.currency}
        assistantName={assistantName}
        onSuccess={() => {
          setShowRefundModal(false);
          setRefundFeedback(
            'Refund initiated. It may take a few days to appear on your card.',
          );
          fetchData();
        }}
      />
      {receiptUrls && (
        <ReceiptModal urls={receiptUrls} onClose={() => setReceiptUrls(null)} />
      )}
      <DismissTodoModal
        show={showCancelModal}
        todo={cancelTodo}
        onClose={() => {
          setShowCancelModal(false);
          setCancelTodo(null);
        }}
        onConfirm={handleCancelConfirm}
      />
    </div>
  );
};

export default PurchasePotSection;
